import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUserCircle, 
  faEnvelope, 
  faShieldAlt, 
  faPhone,
  faGear,
  faBell,
  faDatabase,
  faCamera,
  faImage,
  faCircleCheck,
  faRotate,
  faPalette,
  faRocket,
  faFingerprint,
  faCircleInfo,
  faChevronRight,
  faChevronLeft,
  faClock,
  faFileInvoice,
  faCheck,
  faEnvelopeCircleCheck,
  faPaperPlane,
  faKey,
  faQrcode
} from '@fortawesome/free-solid-svg-icons';
import { startRegistration } from '@simplewebauthn/browser';
import Sidebar from '../Sidebar/Sidebar';
import axios from 'axios';
import { toast } from 'react-toastify';
import { buildApiUrl } from '../../config/api.config';

const Settings = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [userDetails, setUserDetails] = useState(null);
  const [restaurantData, setRestaurantData] = useState(null);
  const [isUpdatingLogo, setIsUpdatingLogo] = useState(false);
  const [newLogo, setNewLogo] = useState(null);
  const [isUpdatingProfilePhoto, setIsUpdatingProfilePhoto] = useState(false);
  const [newProfilePhoto, setNewProfilePhoto] = useState(null);
  const [activeSegment, setActiveSegment] = useState('profile');
  const [isProcessingCheckout, setIsProcessingCheckout] = useState(false);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [isRegisteringMFA, setIsRegisteringMFA] = useState(false);
  const [hasPasskey, setHasPasskey] = useState(false);
  const [hasTotp, setHasTotp] = useState(false);
  const [isRegisteringTotp, setIsRegisteringTotp] = useState(false);
  const [totpQrCode, setTotpQrCode] = useState(null);
  const [totpInput, setTotpInput] = useState('');
  const [invoiceSettings, setInvoiceSettings] = useState({
    primaryColor: '#1a1a2e',
    accentColor: '#e94560',
    headerText: '',
    invoiceTagline: '',
    footerText: '',
    showLogo: true,
    contactVisible: true,
    layoutId: '1'
  });
  const [isUpdatingInvoice, setIsUpdatingInvoice] = useState(false);

  // Inject custom scrollbar style to make it explicitly visible
  useEffect(() => {
    if (!document.getElementById('designer-scrollbar-style')) {
        const style = document.createElement('style');
        style.id = 'designer-scrollbar-style';
        style.innerHTML = `
            .designer-scrollbar::-webkit-scrollbar {
                width: 6px;
            }
            .designer-scrollbar::-webkit-scrollbar-track {
                background: transparent;
            }
            .designer-scrollbar::-webkit-scrollbar-thumb {
                background: rgba(255, 255, 255, 0.08);
                border-radius: 10px;
            }
            .designer-scrollbar::-webkit-scrollbar-thumb:hover {
                background: rgba(255, 255, 255, 0.15);
            }
        `;
        document.head.appendChild(style);
    }
  }, []);
  const [emailSettings, setEmailSettings] = useState({
    smtpEmail: '',
    smtpPassword: '',
    useCustomSmtp: false
  });
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
  const [isTestingEmail, setIsTestingEmail] = useState(false);
  // Helper to calculate robust days remaining
  const getDaysRemaining = (expiryDate) => {
    if (!expiryDate) return 0;
    const expiry = new Date(expiryDate);
    const today = new Date();
    
    // Normalize both to midnight to count full days
    expiry.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    
    const diffTime = expiry.getTime() - today.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const activePlan = subscriptionPlans.find(p => p.planId === restaurantData?.currentPlan);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await axios.get(buildApiUrl("subscription-plans"));
        if (response.data.success) {
          const mappedPlans = response.data.plans.map(p => ({
            ...p,
            id: p.planId
          }));
          setSubscriptionPlans(mappedPlans);
        }
      } catch (error) {
        console.error("Error fetching plans:", error);
      }
    };

    fetchPlans();
    const data = localStorage.getItem("userDetails");
    if (data) {
      const parsedUser = JSON.parse(data);
      setUserDetails(parsedUser);
      fetchRestaurantInfo(parsedUser);
      fetchPersonalDetails();
      fetchPaymentHistory(parsedUser);
      fetchActiveSessions();
      fetchMfaStatus();
    }
  }, [location.key]); // Re-fetch data when navigating back to this page

  const fetchPaymentHistory = async (user) => {
    try {
      const selectedRestId = localStorage.getItem("currentRestaurantId");
      const isMaster = user?.role === 'owner' || localStorage.getItem('fromMaster') === 'true';
      let activeRestaurantId = isMaster ? selectedRestId : user?.restaurantId;

      if (activeRestaurantId && typeof activeRestaurantId === 'object') {
          activeRestaurantId = activeRestaurantId._id;
      }

      if (activeRestaurantId) {
        const response = await axios.get(buildApiUrl(`subscription/history/${activeRestaurantId}`));
        if (response.data.success) {
          setPaymentHistory(response.data.payments);
        }
      }
    } catch (error) {
      console.error("Error fetching payment history:", error);
    }
  };

  const fetchActiveSessions = async () => {
    try {
      const token = localStorage.getItem("token");
      if (token) {
        const response = await axios.get(buildApiUrl("security/sessions"), {
          headers: { "auth-token": token }
        });
        if (response.data.success) {
          setActiveSessions(response.data.sessions);
        }
      }
    } catch (error) {
      console.error("Error fetching active sessions:", error);
    }
  };

  const fetchMfaStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      if (token) {
        const response = await axios.get(buildApiUrl("security/webauthn/status"), {
          headers: { "auth-token": token }
        });
        if (response.data.success) {
          setHasPasskey(response.data.hasPasskey);
        }

        const totpResp = await axios.get(buildApiUrl("security/totp/status"), {
          headers: { "auth-token": token }
        });
        if (totpResp.data.success) {
          setHasTotp(totpResp.data.totpEnabled);
        }
      }
    } catch (error) {
      console.error("Error fetching MFA status:", error);
    }
  };

  const handleTotpSetup = async () => {
    try {
      setIsRegisteringTotp(true);
      const token = localStorage.getItem("token");
      const resp = await axios.post(buildApiUrl("security/totp/generate"), {}, {
        headers: { "auth-token": token }
      });
      if (resp.data.success) {
        setTotpQrCode(resp.data.qrCode);
      }
    } catch (error) {
      console.error("TOTP Setup Error:", error);
      toast.error("Error generating Authenticator QR code");
      setIsRegisteringTotp(false);
    }
  };

  const handleVerifyTotp = async () => {
    try {
      const token = localStorage.getItem("token");
      const resp = await axios.post(buildApiUrl("security/totp/verify"), { token: totpInput }, {
        headers: { "auth-token": token }
      });
      if (resp.data.success) {
        toast.success("Google Authenticator enabled successfully!");
        setHasTotp(true);
        setTotpQrCode(null);
        setTotpInput('');
        setIsRegisteringTotp(false);
      }
    } catch (error) {
      console.error("TOTP Verification Error:", error);
      toast.error(error.response?.data?.message || "Invalid or expired code");
    }
  };

  const cancelTotpSetup = () => {
    setTotpQrCode(null);
    setTotpInput('');
    setIsRegisteringTotp(false);
  };

  const handleMfaSetup = async () => {
    try {
      setIsRegisteringMFA(true);
      const token = localStorage.getItem("token");
      const resp = await axios.get(buildApiUrl("security/webauthn/generate-registration-options"), {
        headers: { "auth-token": token }
      });

      let attResp;
      try {
        attResp = await startRegistration(resp.data.options);
      } catch (error) {
        if (error.name === 'InvalidStateError') {
          toast.error("Authenticator was probably already registered by user");
        } else {
          toast.error("MFA registration failed or was cancelled");
        }
        setIsRegisteringMFA(false);
        return;
      }

      const verificationResp = await axios.post(buildApiUrl("security/webauthn/verify-registration"), attResp, {
        headers: { "auth-token": token }
      });

      if (verificationResp.data.success) {
        toast.success("Multi-Factor Authentication (Biometrics) enabled successfully!");
        setHasPasskey(true);
      } else {
        toast.error("MFA verification failed");
      }
    } catch (error) {
      console.error("MFA Setup Error:", error);
      toast.error(error.response?.data?.error || "Error setting up MFA");
    } finally {
      setIsRegisteringMFA(false);
    }
  };

  const fetchPersonalDetails = async () => {
    try {
      const token = localStorage.getItem("token");
      if (token) {
        const response = await axios.post(buildApiUrl("users/getuser"), {}, {
          headers: { "auth-token": token }
        });
        if (response.data) {
          setUserDetails(response.data);
          // Update local storage if data changed
          localStorage.setItem("userDetails", JSON.stringify(response.data));
        }
      }
    } catch (error) {
      console.error("Error fetching personal details:", error);
    }
  };

  const fetchRestaurantInfo = async (user) => {
    try {
      setRestaurantData(null); // Clear previous state to prevent leakage
      const selectedRestId = localStorage.getItem("currentRestaurantId");
      const isMaster = user?.role === 'owner' || localStorage.getItem('fromMaster') === 'true';
      let activeRestaurantId = isMaster ? selectedRestId : user?.restaurantId;

      // Handle populated restaurantId object
      if (activeRestaurantId && typeof activeRestaurantId === 'object') {
          activeRestaurantId = activeRestaurantId._id;
      }
      
      if (activeRestaurantId) {
        const response = await axios.get(buildApiUrl(`restaurant/${activeRestaurantId}`));
        if (response.data.success) {
          setRestaurantData(response.data.restaurant);
          if (response.data.restaurant.invoiceSettings) {
            setInvoiceSettings(response.data.restaurant.invoiceSettings);
          }
          if (response.data.restaurant.emailSettings) {
            setEmailSettings(response.data.restaurant.emailSettings);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching restaurant info:", error);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewLogo(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleProfilePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewProfilePhoto(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const updateProfilePhoto = async () => {
    if (!newProfilePhoto) return;
    setIsUpdatingProfilePhoto(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(buildApiUrl("users/update-profile-photo"), {
        profilePhoto: newProfilePhoto
      }, {
        headers: { "auth-token": token }
      });
      
      if (response.data.success) {
        setUserDetails(response.data.user);
        setNewProfilePhoto(null);
        localStorage.setItem("userDetails", JSON.stringify(response.data.user));
        toast.success("Profile photo updated!");
        window.dispatchEvent(new Event('identityUpdate'));
      }
    } catch (error) {
      console.error("Error updating profile photo:", error);
      toast.error("Failed to update profile photo");
    } finally {
      setIsUpdatingProfilePhoto(false);
    }
  };

  const updateLogo = async () => {
    if (!newLogo) return;
    setIsUpdatingLogo(true);
    try {
      const selectedRestId = localStorage.getItem("currentRestaurantId");
      const isMaster = userDetails?.role === 'owner' || localStorage.getItem('fromMaster') === 'true';
      let activeRestaurantId = isMaster ? selectedRestId : userDetails?.restaurantId;
      
      if (activeRestaurantId && typeof activeRestaurantId === 'object') {
          activeRestaurantId = activeRestaurantId._id;
      }

      if (!activeRestaurantId) {
          toast.error("Restaurant identity not found. Please select a restaurant.");
          return;
      }
      const response = await axios.put(buildApiUrl(`restaurant/${activeRestaurantId}/logo`), {
        logo: newLogo
      });
      if (response.data.success) {
        setRestaurantData(response.data.restaurant);
        setNewLogo(null);
        toast.success("Branding updated successfully!");
        localStorage.setItem("currentRestaurantLogo", response.data.restaurant.logo);
        localStorage.setItem("cachedRestaurantId", activeRestaurantId);
        localStorage.setItem("currentRestaurantName", response.data.restaurant.name);
        window.dispatchEvent(new Event('identityUpdate'));
      }
    } catch (error) {
      console.error("Error updating logo:", error);
      toast.error("Failed to update branding");
    } finally {
      setIsUpdatingLogo(false);
    }
  };

  const handleRenewSubscription = async (planType) => {
    setIsProcessingCheckout(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(buildApiUrl("stripe/create-checkout-session"), {
        planType
      }, {
        headers: { "auth-token": token }
      });
      
      if (response.data.success && response.data.url) {
        window.location.href = response.data.url;
      } else {
        toast.error("Failed to start checkout");
      }
    } catch (error) {
      console.error("Renewal error:", error);
      toast.error(error.response?.data?.message || "Renewal failed");
    } finally {
      setIsProcessingCheckout(false);
    }
  };

  const saveInvoiceSettings = async () => {
    setIsUpdatingInvoice(true);
    try {
      const selectedRestId = localStorage.getItem("currentRestaurantId");
      const isMaster = userDetails?.role === 'owner' || localStorage.getItem('fromMaster') === 'true';
      let activeRestaurantId = isMaster ? selectedRestId : userDetails?.restaurantId;

      if (activeRestaurantId && typeof activeRestaurantId === 'object') {
          activeRestaurantId = activeRestaurantId._id;
      }

      const response = await axios.put(buildApiUrl(`restaurant/${activeRestaurantId}/invoice-settings`), {
        invoiceSettings
      });

      if (response.data.success) {
        toast.success("Invoice template updated!");
        setRestaurantData(response.data.restaurant);
      }
    } catch (error) {
      console.error("Error updating invoice settings:", error);
      toast.error("Failed to update template");
    } finally {
      setIsUpdatingInvoice(false);
    }
  };

  const saveEmailSettings = async () => {
    setIsUpdatingEmail(true);
    try {
      const selectedRestId = localStorage.getItem("currentRestaurantId");
      const isMaster = userDetails?.role === 'owner' || localStorage.getItem('fromMaster') === 'true';
      let activeRestaurantId = isMaster ? selectedRestId : userDetails?.restaurantId;

      if (activeRestaurantId && typeof activeRestaurantId === 'object') {
          activeRestaurantId = activeRestaurantId._id;
      }

      const response = await axios.put(buildApiUrl(`restaurant/${activeRestaurantId}/email-settings`), {
        emailSettings
      });

      if (response.data.success) {
        toast.success("Email configuration saved!");
        setRestaurantData(response.data.restaurant);
      }
    } catch (error) {
      console.error("Error updating email settings:", error);
      toast.error(error.response?.data?.error || "Failed to save settings");
    } finally {
      setIsUpdatingEmail(false);
    }
  };

  const testEmailSettings = async () => {
    setIsTestingEmail(true);
    try {
      const response = await axios.post(buildApiUrl(`restaurant/test-email`), {
        smtpEmail: emailSettings.smtpEmail,
        smtpPassword: emailSettings.smtpPassword
      });

      if (response.data.success) {
        toast.success(response.data.message);
      }
    } catch (error) {
      console.error("Error testing email settings:", error);
      toast.error(error.response?.data?.error || "Connection test failed");
    } finally {
      setIsTestingEmail(false);
    }
  };

  const styles = {
    container: {
      padding: '1.5rem 2rem',
      maxWidth: '100%',
      height: '100vh',
      margin: '0',
      display: 'flex',
      flexDirection: 'column',
      boxSizing: 'border-box',
      overflow: 'hidden' // Force no body scroll
    },
    navItem: (active) => ({
      display: 'flex',
      alignItems: 'center',
      gap: '0.8rem',
      padding: '0.8rem 1.2rem',
      borderRadius: '12px',
      cursor: 'pointer',
      transition: 'all 0.2s',
      background: active ? 'rgba(255, 65, 53, 0.1)' : 'transparent',
      color: active ? '#ff4135' : 'rgba(255,255,255,0.5)',
      fontWeight: active ? '700' : '500',
      border: `1px solid ${active ? 'rgba(255, 65, 53, 0.2)' : 'transparent'}`,
      whiteSpace: 'nowrap',
      fontSize: '0.9rem'
    }),
    cardBase: {
      background: 'rgba(255, 255, 255, 0.02)',
      backdropFilter: 'blur(20px)',
      border: '1px solid rgba(255, 255, 255, 0.05)',
      borderRadius: '20px',
      padding: '2rem',
      boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      boxSizing: 'border-box'
    },
    label: {
      fontSize: '0.7rem',
      color: 'rgba(255,255,255,0.4)',
      textTransform: 'uppercase',
      letterSpacing: '1.2px',
      fontWeight: '800',
      marginBottom: '0.3rem',
      display: 'block'
    },
    inputMock: {
      padding: '0.8rem 1rem',
      background: 'rgba(255,255,255,0.03)',
      borderRadius: '12px',
      border: '1px solid rgba(255,255,255,0.05)',
      fontSize: '0.9rem',
      color: 'white',
      fontWeight: '600'
    }
  };

  // Replicate access logic for strict gating
  const checkFeatureAccess = (featureName) => {
    // 1. Master Override
    if (userDetails?.role === 'owner' || localStorage.getItem('fromMaster') === 'true') return true;

    // 2. Strict Trial Logic
    if (restaurantData?.currentPlan === 'free_trial') {
         if (restaurantData.createdAt) {
             const createdDate = new Date(restaurantData.createdAt);
             const now = new Date();
             const diffTime = Math.abs(now - createdDate);
             const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
             if (diffDays <= 30) return true;
         }
    }

    // 3. License Expiry Check
    if (restaurantData?.licenseExpiryDate) {
        if (new Date(restaurantData.licenseExpiryDate) < new Date()) {
            return false; 
        }
    } else {
        return false;
    }

    // 4. Feature Check
    // "Custom Branding" covers both Branding tab and Invoice settings
    return restaurantData?.activeFeatures?.includes(featureName);
  };

  const hasBranding = checkFeatureAccess('Custom Branding');

  return (
    <div className="admin-layout" style={{ background: '#101113', height: '100vh', display: 'flex', overflow: 'hidden' }}>
      <Sidebar />
      <main style={{ flex: 1, height: '100vh', overflow: 'hidden' }}>
        <div style={styles.container}>
          {/* Compact Header */}
          <header style={{ marginBottom: '1.5rem', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '1rem', flexShrink: 0 }}>
             <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                   <h1 style={{ fontSize: '1.5rem', fontWeight: '800', margin: 0, color: 'white' }}>Node Configurations</h1>
                   <p style={{ color: 'rgba(255,255,255,0.4)', margin: '0.2rem 0 0 0', fontSize: '0.8rem' }}>Global administration and identity management</p>
                </div>
                <div style={{ padding: '0.4rem 1rem', borderRadius: '8px', background: 'rgba(0, 209, 178, 0.1)', color: '#00d1b2', fontSize: '0.7rem', fontWeight: '800', border: '1px solid rgba(0, 209, 178, 0.1)' }}>
                    <FontAwesomeIcon icon={faCircleCheck} style={{ marginRight: '0.5rem' }} /> SYNC ACTIVE
                </div>
             </div>
          </header>

          <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: '1.5rem', flex: 1, minHeight: 0 }}>
            {/* Compact Sidebar Navigation */}
            <aside style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                <div onClick={() => setActiveSegment('profile')} style={styles.navItem(activeSegment === 'profile')}>
                    <FontAwesomeIcon icon={faUserCircle} style={{width: '18px'}}/> Profile
                </div>
                
                {hasBranding && (
                    <div onClick={() => setActiveSegment('branding')} style={styles.navItem(activeSegment === 'branding')}>
                        <FontAwesomeIcon icon={faPalette} style={{width: '18px'}}/> Branding
                    </div>
                )}

                <div onClick={() => setActiveSegment('security')} style={styles.navItem(activeSegment === 'security')}>
                    <FontAwesomeIcon icon={faShieldAlt} style={{width: '18px'}}/> Security
                </div>
                <div onClick={() => setActiveSegment('infrastructure')} style={styles.navItem(activeSegment === 'infrastructure')}>
                    <FontAwesomeIcon icon={faRocket} style={{width: '18px'}}/> System
                </div>
                <div onClick={() => setActiveSegment('subscription')} style={styles.navItem(activeSegment === 'subscription')}>
                    <FontAwesomeIcon icon={faClock} style={{width: '18px'}}/> Subscription
                </div>
                
                {hasBranding && (
                    <div onClick={() => setActiveSegment('invoice')} style={styles.navItem(activeSegment === 'invoice')}>
                        <FontAwesomeIcon icon={faFileInvoice} style={{width: '18px'}}/> Invoice Template
                    </div>
                )}
                
                <div onClick={() => setActiveSegment('email')} style={styles.navItem(activeSegment === 'email')}>
                    <FontAwesomeIcon icon={faEnvelopeCircleCheck} style={{width: '18px'}}/> Email Config
                </div>
                
                <div style={{ marginTop: 'auto', padding: '1.5rem', borderRadius: '16px', background: 'rgba(255, 65, 53, 0.05)', border: '1px solid rgba(255, 65, 53, 0.1)' }}>
                    <div style={{ color: '#ff4135', fontSize: '0.7rem', fontWeight: '900', textTransform: 'uppercase', marginBottom: '0.3rem' }}>Support</div>
                    <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', margin: 0 }}>Contact master control.</p>
                </div>
            </aside>

            {/* Content Area - Flex based to fit */}
            <section style={{ minHeight: 0, height: '100%', overflow: 'hidden' }}>
                {activeSegment === 'profile' && (
                    <div style={styles.cardBase}>
                        <div style={{ marginBottom: '1.5rem', flexShrink: 0 }}>
                            <h2 style={{ fontSize: '1.5rem', fontWeight: '800', margin: 0 }}>Administrator Profile</h2>
                            <p style={{ color: 'rgba(255,255,255,0.4)', margin: '0.2rem 0 0 0', fontSize: '0.85rem' }}>Manage authorization identity</p>
                        </div>
                        
                        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(200px, 300px) 1fr', gap: '3rem', alignItems: 'start', height: '100%', overflow: 'hidden' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', alignItems: 'center' }}>
                                <div style={{ position: 'relative', width: '100%', maxWidth: '280px', aspectRatio: '1/1' }}>
                                    <div style={{ 
                                        width: '100%', 
                                        height: '100%',
                                        borderRadius: '24px', 
                                        background: 'linear-gradient(135deg, rgba(255, 65, 53, 0.1) 0%, rgba(255, 65, 53, 0.02) 100%)', 
                                        border: '1px solid rgba(255, 65, 53, 0.2)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: userDetails?.profilePhoto || newProfilePhoto ? '0' : '4rem',
                                        fontWeight: '900',
                                        color: '#ff4135',
                                        boxShadow: 'inset 0 0 40px rgba(255, 65, 53, 0.05)',
                                        overflow: 'hidden'
                                    }}>
                                        {newProfilePhoto || userDetails?.profilePhoto ? (
                                            <img src={newProfilePhoto || userDetails.profilePhoto} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        ) : (
                                            userDetails?.name?.charAt(0)
                                        )}
                                    </div>
                                    <label htmlFor="profile-photo-upload" style={{ 
                                        position: 'absolute', bottom: '15px', right: '15px', 
                                        width: '45px', height: '45px', borderRadius: '14px', 
                                        background: 'white', color: 'black', display: 'flex', 
                                        alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                                        boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
                                        zIndex: 10
                                    }}>
                                        <FontAwesomeIcon icon={faCamera} style={{ fontSize: '1rem' }} />
                                        <input id="profile-photo-upload" type="file" hidden accept="image/*" onChange={handleProfilePhotoUpload} />
                                    </label>
                                </div>

                                {newProfilePhoto ? (
                                    <div style={{ display: 'flex', gap: '0.8rem', width: '100%', maxWidth: '280px' }}>
                                        <button onClick={updateProfilePhoto} disabled={isUpdatingProfilePhoto} style={{ 
                                            flex: 1, padding: '0.8rem', borderRadius: '10px', background: '#ff4135', border: 'none', 
                                            color: 'white', fontWeight: '800', cursor: 'pointer', fontSize: '0.9rem'
                                        }}>
                                            {isUpdatingProfilePhoto ? <FontAwesomeIcon icon={faRotate} spin /> : 'Deploy'}
                                        </button>
                                        <button onClick={() => setNewProfilePhoto(null)} style={{ 
                                            padding: '0.8rem', borderRadius: '10px', background: 'rgba(255,255,255,0.05)', border: 'none', 
                                            color: 'white', fontWeight: '700', cursor: 'pointer', fontSize: '0.9rem'
                                        }}>Cancel</button>
                                    </div>
                                ) : (
                                    <div style={{ 
                                        padding: '0.6rem 1.5rem', 
                                        borderRadius: '10px', 
                                        background: 'rgba(255, 65, 53, 0.1)', 
                                        color: '#ff4135', 
                                        fontWeight: '800', 
                                        fontSize: '0.8rem',
                                        border: '1px solid rgba(255, 65, 53, 0.1)',
                                        letterSpacing: '1px'
                                    }}>
                                        {userDetails?.role?.toUpperCase()} ACCESS
                                    </div>
                                )}
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                                <div>
                                    <label style={styles.label}>Full Identity</label>
                                    <div style={styles.inputMock}>{userDetails?.name}</div>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>
                                    <div>
                                        <label style={styles.label}>Primary Contact Email</label>
                                        <div style={styles.inputMock}>
                                            <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: '0.8rem', color: 'rgba(255,255,255,0.3)' }} />
                                            {userDetails?.email}
                                        </div>
                                    </div>
                                    <div>
                                        <label style={styles.label}>Emergency Mobile Number</label>
                                        <div style={styles.inputMock}>
                                            <FontAwesomeIcon icon={faPhone} style={{ marginRight: '0.8rem', color: 'rgba(255,255,255,0.3)' }} />
                                            {userDetails?.contact || '+91 98982 14321'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeSegment === 'branding' && (
                    <div style={styles.cardBase}>
                        <div style={{ marginBottom: '1.5rem', flexShrink: 0  }}>
                            <h2 style={{ fontSize: '1.5rem', fontWeight: '800', margin: 0 }}>Restaurant Identity</h2>
                            <p style={{ color: 'rgba(255,255,255,0.4)', margin: '0.2rem 0 0 0', fontSize: '0.85rem' }}>Configure global branding</p>
                        </div>
                        
                        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(250px, 350px) 1.5fr', gap: '3rem', alignItems: 'center', height: '100%' }}>
                            <div style={{ position: 'relative' }}>
                                <div style={{ 
                                    width: '100%', 
                                    aspectRatio: '1/1',
                                    borderRadius: '24px', 
                                    background: '#151618', 
                                    border: '2px solid rgba(255,255,255,0.05)',
                                    overflow: 'hidden',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '2rem'
                                }}>
                                    {newLogo || restaurantData?.logo ? (
                                        <img src={newLogo || restaurantData?.logo} alt="Branding" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                    ) : (
                                        <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.2)' }}>
                                            <FontAwesomeIcon icon={faImage} style={{ fontSize: '3rem', marginBottom: '1rem' }} />
                                            <div style={{ fontWeight: '800', fontSize: '0.8rem', letterSpacing: '2px' }}>BRAND EMPTY</div>
                                        </div>
                                    )}
                                </div>
                                <label htmlFor="logo-upload-v4" style={{ 
                                    position: 'absolute', bottom: '15px', right: '15px', 
                                    width: '50px', height: '50px', borderRadius: '16px', 
                                    background: 'white', color: 'black', display: 'flex', 
                                    alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                                    boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
                                    zIndex: 10
                                }}>
                                    <FontAwesomeIcon icon={faCamera} style={{ fontSize: '1.1rem' }} />
                                    <input id="logo-upload-v4" type="file" hidden accept="image/*" onChange={handleLogoUpload} />
                                </label>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                                <div style={{ background: 'rgba(0, 209, 178, 0.03)', border: '1px solid rgba(0, 209, 178, 0.1)', padding: '1.5rem', borderRadius: '18px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', color: '#00d1b2', marginBottom: '0.5rem' }}>
                                        <FontAwesomeIcon icon={faRotate} />
                                        <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: '800' }}>Cloud Synchronization</h4>
                                    </div>
                                    <p style={{ margin: 0, fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', lineHeight: '1.6' }}>
                                        Branding assets are propagated to cloud reports and invoices with 0ms latency.
                                    </p>
                                </div>
                                
                                {newLogo ? (
                                    <div style={{ display: 'flex', gap: '1rem' }}>
                                        <button onClick={updateLogo} disabled={isUpdatingLogo} style={{ 
                                            flex: 1, padding: '1rem', borderRadius: '14px', background: '#ff4135', border: 'none', 
                                            color: 'white', fontWeight: '800', cursor: 'pointer', fontSize: '0.9rem'
                                        }}>
                                            {isUpdatingLogo ? <FontAwesomeIcon icon={faRotate} spin /> : <FontAwesomeIcon icon={faCircleCheck} />} Deploy Branding
                                        </button>
                                        <button onClick={() => setNewLogo(null)} style={{ 
                                            padding: '1rem 1.5rem', borderRadius: '14px', background: 'rgba(255,255,255,0.05)', border: 'none', 
                                            color: 'white', fontWeight: '700', cursor: 'pointer'
                                        }}>Discard</button>
                                    </div>
                                ) : (
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                        <div style={{ padding: '1.2rem', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)', background: 'rgba(255,255,255,0.01)' }}>
                                            <div style={{ fontWeight: '800', fontSize: '1rem', marginBottom: '0.2rem', color: 'white' }}>PNG/SVG</div>
                                            <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', fontWeight: '800' }}>Format Type</div>
                                        </div>
                                        <div style={{ padding: '1.2rem', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)', background: 'rgba(255,255,255,0.01)' }}>
                                            <div style={{ fontWeight: '800', fontSize: '1rem', marginBottom: '0.2rem', color: 'white' }}>High Res</div>
                                            <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', fontWeight: '800' }}>Quality Target</div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {activeSegment === 'security' && (
                    <div style={styles.cardBase}>
                        <div style={{ marginBottom: '1.5rem', flexShrink: 0 }}>
                            <h2 style={{ fontSize: '1.5rem', fontWeight: '800', margin: 0 }}>Security Hardening</h2>
                            <p style={{ color: 'rgba(255,255,255,0.4)', margin: '0.2rem 0 0 0', fontSize: '0.85rem' }}>Encryption and access tokens</p>
                        </div>
                        
                        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 1fr) 1.5fr', gap: '3rem', alignItems: 'start', height: '100%', alignContent: 'center' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', alignItems: 'center' }}>
                                <div style={{ 
                                    width: '100%',
                                    maxWidth: '240px',
                                    aspectRatio: '1/1',
                                    borderRadius: '24px', 
                                    background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.1) 0%, rgba(255, 193, 7, 0.02) 100%)', 
                                    border: '1px solid rgba(255, 193, 7, 0.2)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '4rem',
                                    color: '#ffc107',
                                    boxShadow: 'inset 0 0 40px rgba(255, 193, 7, 0.05)'
                                }}>
                                    <FontAwesomeIcon icon={faShieldAlt} />
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontWeight: '800', fontSize: '1.1rem', color: 'white' }}>Level 4 Security</div>
                                    <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '1px', marginTop: '0.2rem' }}>Standard Coverage</div>
                                </div>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                    <label style={styles.label}>Authentication Methods</label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '1.2rem', padding: '1.2rem', background: 'rgba(255, 65, 53, 0.03)', border: '1px solid rgba(255, 65, 53, 0.1)', borderRadius: '18px' }}>
                                    <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(255, 65, 53, 0.1)', color: '#ff4135', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>
                                        <FontAwesomeIcon icon={faFingerprint} />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: '800', fontSize: '1rem' }}>Multi-Factor Auth</div>
                                        <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem' }}>Biometric verification</div>
                                    </div>
                                    <button 
                                        onClick={!hasPasskey ? handleMfaSetup : undefined} 
                                        disabled={isRegisteringMFA}
                                        style={{ 
                                            padding: '0.6rem 1.2rem', 
                                            borderRadius: '10px', 
                                            background: hasPasskey ? 'rgba(0, 209, 178, 0.1)' : 'transparent', 
                                            border: `1px solid ${hasPasskey ? 'rgba(0, 209, 178, 0.3)' : 'rgba(255,255,255,0.1)'}`, 
                                            color: hasPasskey ? '#00d1b2' : 'white', 
                                            fontWeight: '800', 
                                            fontSize: '0.8rem', 
                                            cursor: hasPasskey ? 'default' : 'pointer' 
                                        }}
                                    >
                                        {isRegisteringMFA ? <FontAwesomeIcon icon={faRotate} spin /> : (hasPasskey ? 'Active' : 'Setup')}
                                    </button>
                                </div>

                                {/* TOTP Section */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '1.2rem', padding: '1.2rem', background: 'rgba(74, 144, 226, 0.03)', border: '1px solid rgba(74, 144, 226, 0.1)', borderRadius: '18px' }}>
                                    <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(74, 144, 226, 0.1)', color: '#4a90e2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>
                                        <FontAwesomeIcon icon={faQrcode} />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: '800', fontSize: '1rem' }}>Google Authenticator</div>
                                        <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem' }}>Time-based codes (TOTP)</div>
                                    </div>
                                    <button 
                                        onClick={!hasTotp ? handleTotpSetup : undefined} 
                                        disabled={isRegisteringTotp}
                                        style={{ 
                                            padding: '0.6rem 1.2rem', 
                                            borderRadius: '10px', 
                                            background: hasTotp ? 'rgba(0, 209, 178, 0.1)' : 'transparent', 
                                            border: `1px solid ${hasTotp ? 'rgba(0, 209, 178, 0.3)' : 'rgba(255,255,255,0.1)'}`, 
                                            color: hasTotp ? '#00d1b2' : 'white', 
                                            fontWeight: '800', 
                                            fontSize: '0.8rem', 
                                            cursor: hasTotp ? 'default' : 'pointer' 
                                        }}
                                    >
                                        {isRegisteringTotp ? <FontAwesomeIcon icon={faRotate} spin /> : (hasTotp ? 'Active' : 'Setup')}
                                    </button>
                                </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* TOTP Modal */}
                {totpQrCode && (
                    <div style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(10px)', zIndex: 1000,
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                        <div style={{
                            background: '#1a1a2e', padding: '2.5rem', borderRadius: '24px',
                            border: '1px solid rgba(255,255,255,0.1)', maxWidth: '400px', width: '90%',
                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem',
                            boxShadow: '0 20px 40px rgba(0,0,0,0.4)'
                        }}>
                            <div style={{ textAlign: 'center' }}>
                                <h3 style={{ fontSize: '1.5rem', fontWeight: '800', color: 'white', margin: '0 0 0.5rem 0' }}>Scan QR Code</h3>
                                <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.5)', margin: 0, lineHeight: '1.4' }}>
                                    Open Google Authenticator or Authy on your phone and scan this code.
                                </p>
                            </div>

                            <div style={{ background: 'white', padding: '1rem', borderRadius: '16px' }}>
                                <img src={totpQrCode} alt="TOTP QR Code" style={{ width: '200px', height: '200px' }} />
                            </div>

                            <div style={{ width: '100%' }}>
                                <label style={{ ...styles.label, textAlign: 'center', display: 'block', marginBottom: '0.8rem' }}>Enter the 6-digit code</label>
                                <input
                                    type="text"
                                    maxLength="6"
                                    value={totpInput}
                                    onChange={(e) => setTotpInput(e.target.value.replace(/\D/g, ''))}
                                    style={{
                                        width: '100%', padding: '1rem', background: 'rgba(255,255,255,0.05)',
                                        border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px',
                                        color: 'white', fontSize: '1.5rem', letterSpacing: '8px', textAlign: 'center',
                                        fontWeight: '800'
                                    }}
                                    placeholder="000000"
                                />
                            </div>

                            <div style={{ display: 'flex', gap: '1rem', width: '100%', marginTop: '0.5rem' }}>
                                <button onClick={cancelTotpSetup} style={{ flex: 1, padding: '1rem', background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: 'white', borderRadius: '12px', fontWeight: '800', cursor: 'pointer' }}>Cancel</button>
                                <button onClick={handleVerifyTotp} disabled={totpInput.length !== 6} style={{ flex: 1, padding: '1rem', background: 'var(--tf-primary)', border: 'none', color: 'white', borderRadius: '12px', fontWeight: '800', cursor: totpInput.length === 6 ? 'pointer' : 'not-allowed', opacity: totpInput.length === 6 ? 1 : 0.5 }}>Verify</button>
                            </div>
                        </div>
                    </div>
                )}

                {activeSegment === 'infrastructure' && (
                    <div style={styles.cardBase}>
                        <div style={{ marginBottom: '1.5rem', flexShrink: 0  }}>
                            <h2 style={{ fontSize: '1.5rem', fontWeight: '800', margin: 0 }}>System Infrastructure</h2>
                            <p style={{ color: 'rgba(255,255,255,0.4)', margin: '0.2rem 0 0 0', fontSize: '0.85rem' }}>Monitor node performance</p>
                        </div>
                        
                        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 1fr) 1.5fr', gap: '3rem', alignItems: 'start', height: '100%', alignContent: 'center' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', alignItems: 'center' }}>
                                <div style={{ 
                                    width: '100%',
                                    maxWidth: '240px',
                                    aspectRatio: '1/1',
                                    borderRadius: '24px', 
                                    background: 'linear-gradient(135deg, rgba(0, 209, 178, 0.1) 0%, rgba(0, 209, 178, 0.02) 100%)', 
                                    border: '1px solid rgba(0, 209, 178, 0.2)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '4rem',
                                    color: '#00d1b2',
                                    boxShadow: 'inset 0 0 40px rgba(0, 209, 178, 0.05)'
                                }}>
                                    <FontAwesomeIcon icon={faRocket} />
                                </div>
                                <div style={{ width: '100%', background: 'rgba(255,255,255,0.02)', padding: '1.2rem', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                                        <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', fontWeight: '800' }}>SYNC LOAD</span>
                                        <span style={{ fontSize: '0.7rem', color: '#00d1b2', fontWeight: '800' }}>85%</span>
                                    </div>
                                    <div style={{ width: '100%', height: '5px', background: 'rgba(255,255,255,0.05)', borderRadius: '3px', overflow: 'hidden' }}>
                                        <div style={{ width: '85%', height: '100%', background: 'linear-gradient(90deg, #00d1b2, #00ffcc)', borderRadius: '3px' }}></div>
                                    </div>
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.2rem' }}>
                                <div style={{ gridColumn: 'span 2', padding: '1.5rem', borderRadius: '18px', background: 'rgba(0, 209, 178, 0.03)', border: '1px solid rgba(0, 209, 178, 0.1)' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', color: '#00d1b2', marginBottom: '0.8rem' }}>
                                        <FontAwesomeIcon icon={faDatabase} />
                                        <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: '800' }}>Core Data Engine</h4>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                                        <div>
                                            <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', marginBottom: '0.2rem' }}>Global Status</div>
                                            <div style={{ fontSize: '1rem', fontWeight: '800', color: 'white' }}>OPERATIONAL</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', marginBottom: '0.2rem' }}>Avg Latency</div>
                                            <div style={{ fontSize: '1rem', fontWeight: '800', color: 'white' }}>14.2ms</div>
                                        </div>
                                    </div>
                                </div>

                                <div style={{ padding: '1.5rem', borderRadius: '18px', border: '1px solid rgba(255,255,255,0.05)', background: 'rgba(255,255,255,0.02)' }}>
                                    <div style={{ fontSize: '1.2rem', fontWeight: '900', color: 'white' }}>99.9%</div>
                                    <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', fontWeight: '800', marginTop: '0.4rem' }}>Uptime Cluster</div>
                                </div>
                                <div style={{ padding: '1.5rem', borderRadius: '18px', border: '1px solid rgba(255,255,255,0.05)', background: 'rgba(255,255,255,0.02)' }}>
                                    <div style={{ fontSize: '1.2rem', fontWeight: '900', color: 'white' }}>SSL v3</div>
                                    <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', fontWeight: '800', marginTop: '0.4rem' }}>Transport Layer</div>
                                </div>

                                <div style={{ gridColumn: 'span 2', background: 'rgba(255, 193, 7, 0.05)', padding: '1.2rem', borderRadius: '16px', border: '1px solid rgba(255, 193, 7, 0.2)', display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                                    <FontAwesomeIcon icon={faCircleInfo} style={{ color: '#ffc107' }} />
                                    <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)' }}>Node kinetics exceeding standard by 14%.</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeSegment === 'subscription' && (
                    <div style={styles.cardBase}>
                        {/* Header Summary Row */}
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            padding: '1.5rem', 
                            background: 'rgba(255,255,255,0.02)', 
                            borderRadius: '16px',
                            border: '1px solid rgba(255,255,255,0.05)',
                            marginBottom: '1.5rem'
                        }}>
                            <div style={{ display: 'flex', gap: '3rem' }}>
                                <div>
                                    <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', fontWeight: '700', textTransform: 'uppercase', marginBottom: '0.4rem' }}>Current Plan</div>
                                    <div style={{ fontSize: '1.2rem', fontWeight: '800', color: 'white', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                                        {(() => {
                                             if (restaurantData?.currentPlan === 'free_trial') return '30-Day Free Trial';
                                             if (restaurantData?.currentPlan === 'yearly_basic') return 'Yearly Basic'; // Fallback for legacy
                                             return activePlan?.name || 'Standard Plan';
                                        })()}
                                        <span style={{ fontSize: '0.6rem', padding: '2px 8px', borderRadius: '4px', background: 'rgba(0, 209, 178, 0.1)', color: '#00d1b2', fontWeight: '800' }}>ACTIVE</span>
                                    </div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', fontWeight: '700', textTransform: 'uppercase', marginBottom: '0.4rem' }}>Remaining</div>
                                    <div style={{ fontSize: '1.2rem', fontWeight: '800', color: 'white' }}>
                                        {getDaysRemaining(restaurantData?.licenseExpiryDate)} Days
                                    </div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', fontWeight: '700', textTransform: 'uppercase', marginBottom: '0.4rem' }}>Expiry Date</div>
                                    <div style={{ fontSize: '1.2rem', fontWeight: '800', color: 'white' }}>
                                        {restaurantData?.licenseExpiryDate ? new Date(restaurantData.licenseExpiryDate).toLocaleDateString() : 'N/A'}
                                    </div>
                                </div>
                            </div>
                            <button 
                                onClick={() => navigate('/admin/subscription/plans')}
                                style={{ 
                                    padding: '0.8rem 1.5rem', borderRadius: '10px', 
                                    background: 'var(--tf-primary)', border: 'none', color: 'white', 
                                    fontWeight: '800', cursor: 'pointer', fontSize: '0.85rem'
                                }}
                            >
                                Renew Plan
                            </button>
                        </div>

                        {/* Main Details Section */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                            {/* Node Info + Warning Row */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                                <div style={{ 
                                    padding: '1.5rem', borderRadius: '20px', 
                                    background: 'rgba(255,255,255,0.01)', border: '1px solid rgba(255,255,255,0.05)'
                                }}>
                                    <h4 style={{ fontSize: '0.9rem', fontWeight: '800', color: 'white', marginBottom: '1rem' }}>Node Information</h4>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)' }}>Node ID</span>
                                            <span style={{ fontSize: '0.75rem', color: 'white', fontWeight: '600' }}>{restaurantData?._id?.substring(0, 12)}...</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)' }}>Status</span>
                                            <span style={{ fontSize: '0.75rem', color: '#00d1b2', fontWeight: '800' }}>VERIFIED</span>
                                        </div>
                                    </div>
                                </div>
                                
                                <div style={{ 
                                    padding: '1.5rem', borderRadius: '20px', 
                                    background: 'rgba(255, 193, 7, 0.03)', border: '1px solid rgba(255, 193, 7, 0.1)', 
                                    display: 'flex', gap: '0.8rem', alignItems: 'flex-start'
                                }}>
                                    <FontAwesomeIcon icon={faCircleInfo} style={{ color: '#ffc107', marginTop: '0.2rem', flexShrink: 0 }} />
                                    <p style={{ margin: 0, fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', lineHeight: '1.6' }}>
                                        Automated subscription management is active. Please ensure your payment method is valid to avoid service interruption.
                                    </p>
                                </div>
                            </div>

                            {/* Features Section - Full Width */}
                            <div style={{ background: 'rgba(255,255,255,0.01)', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.05)', padding: '2rem' }}>
                                <h3 style={{ fontSize: '1rem', fontWeight: '800', color: 'white', marginBottom: '1.5rem' }}>Plan Features & Capabilities</h3>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                                    {/* Trial Logic for display */}
                                    {(() => {
                                        const featureTitles = {
                                            'Inventory Management': 'Stock & Raw Materials',
                                            'Invoicing & GST Billing': 'GST Invoices & Reports',
                                            'Kitchen Display (KDS)': 'Live Kitchen Screen',
                                            'Table Reservation': 'Table Booking Manager',
                                            'Point of Sale (POS)': 'Smart Billing Terminal',
                                            'Staff Management': 'Team Roles & Logins',
                                            'Advanced Analytics': 'Profit & Sales Reports',
                                            'Customer Database': 'Customer History',
                                            'Review Management': 'Ratings & Feedback',
                                            'Attendance & Payroll': 'Attendance & Salary',
                                            'Advanced Security': 'Data Security Shield'
                                        };
                                        const isTrial = restaurantData?.currentPlan === 'free_trial' && 
                                            (restaurantData?.createdAt && (new Date() - new Date(restaurantData.createdAt)) < 30 * 24 * 60 * 60 * 1000);
                                        const displayFeatures = isTrial ? [
                                            'Inventory Management', 'Invoicing & GST Billing', 'Point of Sale (POS)', 
                                            'Staff Management', 'Kitchen Display (KDS)', 'Table Reservation',
                                            'Advanced Analytics', 'Customer Database', 'Review Management',
                                            'Attendance & Payroll', 'Advanced Security'
                                        ] : (activePlan?.features || restaurantData?.activeFeatures || []);
                                        return displayFeatures.map((feature, i) => (
                                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>
                                                <FontAwesomeIcon icon={faCheck} style={{ color: '#00d1b2', fontSize: '0.8rem' }} />
                                                {featureTitles[feature] || feature} {isTrial && <span style={{fontSize: '0.6rem', background: '#333', padding: '2px 4px', borderRadius: '4px'}}>TRIAL</span>}
                                            </div>
                                        ));
                                    })()}
                                </div>
                            </div>

                            {/* Transaction History - Full Width */}
                            <div style={{ background: 'rgba(255,255,255,0.01)', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.05)', padding: '2rem' }}>
                                <h3 style={{ fontSize: '1.2rem', fontWeight: '800', marginBottom: '1.5rem', color: 'white' }}>Transaction History</h3>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem', maxHeight: '300px', overflowY: 'auto' }}>
                                    {paymentHistory.length > 0 ? paymentHistory.map((payment, i) => (
                                        <div key={i} style={{ 
                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center', 
                                            padding: '1rem', background: 'rgba(255,255,255,0.02)', 
                                            borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)' 
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                                <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(88, 101, 242, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#5865F2' }}>
                                                    <FontAwesomeIcon icon={faRotate} style={{ fontSize: '0.8rem' }} />
                                                </div>
                                                <div>
                                                    <div style={{ fontWeight: '700', color: 'white', fontSize: '0.9rem' }}>{payment.planName}</div>
                                                    <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)' }}>{new Date(payment.paymentDate).toLocaleDateString()} · {payment.paymentMethod}</div>
                                                </div>
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                                <div style={{ fontWeight: '800', color: 'white', fontSize: '0.9rem' }}>₹{payment.amount}</div>
                                                <div style={{ fontSize: '0.6rem', color: '#00d1b2', fontWeight: '800' }}>{payment.status.toUpperCase()}</div>
                                            </div>
                                        </div>
                                    )) : (
                                        <div style={{ textAlign: 'center', padding: '2rem', color: 'rgba(255,255,255,0.2)', fontSize: '0.85rem' }}>
                                            No payment history found for this node.
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}



                {activeSegment === 'email' && (
                    <div style={styles.cardBase}>
                        <div style={{ marginBottom: '1.5rem', flexShrink: 0 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <h2 style={{ fontSize: '1.5rem', fontWeight: '800', margin: 0 }}>Email Configuration</h2>
                                    <p style={{ color: 'rgba(255,255,255,0.4)', margin: '0.2rem 0 0 0', fontSize: '0.85rem' }}>Send notifications via your own SMTP server</p>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                    <span style={{ fontSize: '0.8rem', fontWeight: '700', color: emailSettings.useCustomSmtp ? '#00d1b2' : 'rgba(255,255,255,0.3)' }}>
                                        {emailSettings.useCustomSmtp ? 'CUSTOM SMTP ENABLED' : 'USING PLATFORM DEFAULT'}
                                    </span>
                                    <label className="switch" style={{ position: 'relative', display: 'inline-block', width: '50px', height: '24px' }}>
                                        <input 
                                            type="checkbox" 
                                            checked={emailSettings.useCustomSmtp}
                                            onChange={(e) => setEmailSettings({ ...emailSettings, useCustomSmtp: e.target.checked })}
                                            style={{ opacity: 0, width: 0, height: 0 }}
                                        />
                                        <span style={{ 
                                            position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0, 
                                            backgroundColor: emailSettings.useCustomSmtp ? '#00d1b2' : '#ccc', 
                                            transition: '.4s', borderRadius: '34px' 
                                        }}>
                                            <span style={{ 
                                                position: 'absolute', content: '""', height: '18px', width: '18px', left: '3px', bottom: '3px', 
                                                backgroundColor: 'white', transition: '.4s', borderRadius: '50%',
                                                transform: emailSettings.useCustomSmtp ? 'translateX(26px)' : 'translateX(0)'
                                            }}></span>
                                        </span>
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '2rem', flex: 1, minHeight: 0 }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', overflowY: 'auto', paddingRight: '0.5rem' }}>
                                <div style={{ background: 'rgba(255,255,255,0.02)', padding: '1.5rem', borderRadius: '18px', border: '1px solid rgba(255,255,255,0.05)' }}>
                                    <div style={{ marginBottom: '1.5rem' }}>
                                        <label style={styles.label}>Gmail Address</label>
                                        <div style={{ position: 'relative' }}>
                                            <FontAwesomeIcon icon={faEnvelope} style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.2)' }} />
                                            <input 
                                                type="email"
                                                value={emailSettings.smtpEmail}
                                                onChange={(e) => setEmailSettings({ ...emailSettings, smtpEmail: e.target.value })}
                                                placeholder="your-restaurant@gmail.com"
                                                style={{ ...styles.inputMock, width: '100%', paddingLeft: '2.8rem', background: 'rgba(0,0,0,0.2)' }}
                                            />
                                        </div>
                                    </div>

                                    <div style={{ marginBottom: '1.5rem' }}>
                                        <label style={styles.label}>Google App Password</label>
                                        <div style={{ position: 'relative' }}>
                                            <FontAwesomeIcon icon={faKey} style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.2)' }} />
                                            <input 
                                                type="password"
                                                value={emailSettings.smtpPassword}
                                                onChange={(e) => setEmailSettings({ ...emailSettings, smtpPassword: e.target.value })}
                                                placeholder="xxxx xxxx xxxx xxxx"
                                                style={{ ...styles.inputMock, width: '100%', paddingLeft: '2.8rem', background: 'rgba(0,0,0,0.2)' }}
                                            />
                                        </div>
                                        <p style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)', marginTop: '0.5rem' }}>
                                            * Never use your main account password. Always use a 16-digit App Password.
                                        </p>
                                    </div>

                                    <div style={{ display: 'flex', gap: '1rem' }}>
                                        <button 
                                            onClick={testEmailSettings}
                                            disabled={isTestingEmail}
                                            style={{ 
                                                flex: 1, padding: '0.8rem', borderRadius: '12px', 
                                                background: 'rgba(255,255,255,0.05)', color: 'white', 
                                                fontWeight: '700', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer'
                                            }}
                                        >
                                            {isTestingEmail ? <FontAwesomeIcon icon={faRotate} spin /> : <><FontAwesomeIcon icon={faPaperPlane} style={{marginRight: '0.5rem'}}/> Test Connection</>}
                                        </button>
                                        <button 
                                            onClick={saveEmailSettings}
                                            disabled={isUpdatingEmail}
                                            style={{ 
                                                flex: 1, padding: '0.8rem', borderRadius: '12px', 
                                                background: '#ff4135', color: 'white', 
                                                fontWeight: '800', border: 'none', cursor: 'pointer'
                                            }}
                                        >
                                            {isUpdatingEmail ? <FontAwesomeIcon icon={faRotate} spin /> : 'Save Configuration'}
                                        </button>
                                    </div>
                                </div>

                                <div style={{ padding: '1rem', background: 'rgba(255, 193, 7, 0.05)', border: '1px solid rgba(255, 193, 7, 0.2)', borderRadius: '12px', display: 'flex', gap: '1rem' }}>
                                    <FontAwesomeIcon icon={faCircleInfo} style={{ color: '#ffc107', marginTop: '0.2rem' }} />
                                    <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)', lineHeight: '1.5' }}>
                                        <strong>Note:</strong> Table ready notifications, reservation confirmations, and invoice emails will use this account once enabled.
                                    </div>
                                </div>
                            </div>

                            <div style={{ background: 'rgba(0,0,0,0.2)', padding: '1.5rem', borderRadius: '18px', border: '1px solid rgba(255,255,255,0.05)' }}>
                                <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: '800', color: 'white' }}>How to generate App Password?</h3>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
                                    {[
                                        { step: 1, text: "Enable 2-Step Verification on your Google Account security settings." },
                                        { step: 2, text: "Navigate to 'App passwords' section (Search for it in Google Account search)." },
                                        { step: 3, text: "Select 'Other (Custom name)' and enter 'TastyFlow'." },
                                        { step: 4, text: "Generate and copy the 16-character code into the password field." }
                                    ].map((item) => (
                                        <div key={item.step} style={{ display: 'flex', gap: '1rem' }}>
                                            <div style={{ 
                                                minWidth: '24px', height: '24px', borderRadius: '50%', 
                                                background: '#ff4135', color: 'white', display: 'flex', 
                                                alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: '900' 
                                            }}>{item.step}</div>
                                            <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.5)', lineHeight: '1.4' }}>{item.text}</div>
                                        </div>
                                    ))}
                                    <div style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(255,255,255,0.02)', borderRadius: '12px' }}>
                                        <p style={{ margin: 0, fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)', textAlign: 'center' }}>
                                            Need help? <a href="https://support.google.com/accounts/answer/185833" target="_blank" rel="noreferrer" style={{ color: '#ff4135', textDecoration: 'none' }}>Visit Google Support</a>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                
                {activeSegment === 'invoice' && (
                    <div style={styles.cardBase}>
                        <div style={{ marginBottom: '1.5rem', flexShrink: 0 }}>
                            <h2 style={{ fontSize: '1.5rem', fontWeight: '800', margin: 0 }}>Invoice Designer</h2>
                            <p style={{ color: 'rgba(255,255,255,0.4)', margin: '0.2rem 0 0 0', fontSize: '0.85rem' }}>Customize your billing identity</p>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2.5rem', height: '100%', overflow: 'hidden' }}>
                            {/* Configuration Panel */}
                            <div className="designer-scrollbar" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', overflowY: 'scroll', paddingRight: '1rem', height: '100%', paddingBottom: '2rem' }}>
                                <div>
                                    <label style={styles.label}>Theme Colors</label>
                                    <div style={{ display: 'flex', gap: '1rem' }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', marginBottom: '0.3rem' }}>Primary</div>
                                            <input type="color" value={invoiceSettings.primaryColor} onChange={(e) => setInvoiceSettings({...invoiceSettings, primaryColor: e.target.value})} style={{ width: '100%', height: '40px', border: 'none', borderRadius: '8px', cursor: 'pointer', background: 'transparent' }} />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', marginBottom: '0.3rem' }}>Accent</div>
                                            <input type="color" value={invoiceSettings.accentColor} onChange={(e) => setInvoiceSettings({...invoiceSettings, accentColor: e.target.value})} style={{ width: '100%', height: '40px', border: 'none', borderRadius: '8px', cursor: 'pointer', background: 'transparent' }} />
                                        </div>
                                    </div>
                                    
                                    {/* Preset Palettes */}
                                    <div style={{ marginTop: '1rem' }}>
                                        <div style={{ fontSize: '0.7rem', fontWeight: '700', color: 'rgba(255,255,255,0.4)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Preset Themes</div>
                                        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                                            {[
                                                { name: 'Classic', primary: '#1a1a2e', accent: '#e94560' },
                                                { name: 'Ocean', primary: '#2c3e50', accent: '#3498db' },
                                                { name: 'Premium', primary: '#111111', accent: '#d4af37' },
                                                { name: 'Professional', primary: '#2c3e50', accent: '#e74c3c' },
                                                { name: 'Graphite', primary: '#1f2937', accent: '#10b981' },
                                                { name: 'Midnight', primary: '#2c3e50', accent: '#bdc3c7' }
                                            ].map((p, i) => (
                                                <div 
                                                    key={i}
                                                    onClick={() => setInvoiceSettings({...invoiceSettings, primaryColor: p.primary, accentColor: p.accent})}
                                                    title={p.name}
                                                    style={{
                                                        width: '28px', height: '28px', borderRadius: '50%',
                                                        background: `linear-gradient(135deg, ${p.primary} 50%, ${p.accent} 50%)`,
                                                        cursor: 'pointer',
                                                        border: (invoiceSettings.primaryColor === p.primary && invoiceSettings.accentColor === p.accent) ? '2px solid white' : '1px solid rgba(255,255,255,0.1)',
                                                        boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
                                                        transition: 'transform 0.2s',
                                                    }}
                                                    onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                                                    onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <label style={styles.label}>Header Message (Optional)</label>
                                    <input type="text" placeholder="e.g. Special Weekend Offer!" value={invoiceSettings.headerText} onChange={(e) => setInvoiceSettings({...invoiceSettings, headerText: e.target.value})} style={{ ...styles.inputMock, width: '100%', background: 'rgba(255,255,255,0.05)' }} />
                                </div>

                                <div>
                                    <label style={styles.label}>Tagline</label>
                                    <input type="text" placeholder="e.g. Premium Dining Experience" value={invoiceSettings.invoiceTagline} onChange={(e) => setInvoiceSettings({...invoiceSettings, invoiceTagline: e.target.value})} style={{ ...styles.inputMock, width: '100%', background: 'rgba(255,255,255,0.05)' }} />
                                </div>

                                <div>
                                    <label style={styles.label}>Footer Note</label>
                                    <textarea value={invoiceSettings.footerText} onChange={(e) => setInvoiceSettings({...invoiceSettings, footerText: e.target.value})} style={{ ...styles.inputMock, width: '100%', minHeight: '80px', background: 'rgba(255,255,255,0.05)', resize: 'none' }} />
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', cursor: 'pointer' }}>
                                        <input type="checkbox" checked={invoiceSettings.showLogo} onChange={(e) => setInvoiceSettings({...invoiceSettings, showLogo: e.target.checked})} style={{ width: '18px', height: '18px', accentColor: '#ff4135' }} />
                                        <span style={{ fontSize: '0.9rem', fontWeight: '600' }}>Show Logo on Bill</span>
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', cursor: 'pointer' }}>
                                        <input type="checkbox" checked={invoiceSettings.contactVisible} onChange={(e) => setInvoiceSettings({...invoiceSettings, contactVisible: e.target.checked})} style={{ width: '18px', height: '18px', accentColor: '#ff4135' }} />
                                        <span style={{ fontSize: '0.9rem', fontWeight: '600' }}>Show Contact Details</span>
                                    </label>
                                </div>

                                <button onClick={saveInvoiceSettings} disabled={isUpdatingInvoice} style={{ padding: '1rem', borderRadius: '14px', background: '#ff4135', border: 'none', color: 'white', fontWeight: '800', cursor: 'pointer', marginTop: 'auto' }}>
                                    {isUpdatingInvoice ? <FontAwesomeIcon icon={faRotate} spin /> : <><FontAwesomeIcon icon={faCircleCheck} style={{marginRight: '0.5rem'}}/> Deploy Design</>}
                                </button>
                            </div>

                            {/* Preview Panel */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', height: '100%', overflow: 'hidden' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <label style={styles.label}>Live Preview (A4 Scale)</label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', background: 'rgba(255,255,255,0.03)', padding: '0.4rem 0.8rem', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.05)' }}>
                                        <button 
                                            onClick={() => {
                                                const current = parseInt(invoiceSettings.layoutId || '1');
                                                const next = current > 1 ? current - 1 : 4;
                                                setInvoiceSettings({...invoiceSettings, layoutId: next.toString()});
                                            }}
                                            style={{ background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', padding: '0.2rem' }}
                                        >
                                            <FontAwesomeIcon icon={faChevronLeft} />
                                        </button>
                                        <span style={{ fontSize: '0.75rem', fontWeight: '800', width: '80px', textAlign: 'center', color: '#ff4135' }}>
                                            {['Standard', 'Modern', 'Minimal', 'Compact'][parseInt(invoiceSettings.layoutId || '1') - 1]}
                                        </span>
                                        <button 
                                            onClick={() => {
                                                const current = parseInt(invoiceSettings.layoutId || '1');
                                                const next = current < 4 ? current + 1 : 1;
                                                setInvoiceSettings({...invoiceSettings, layoutId: next.toString()});
                                            }}
                                            style={{ background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', padding: '0.2rem' }}
                                        >
                                            <FontAwesomeIcon icon={faChevronRight} />
                                        </button>
                                    </div>
                                </div>
                                <div style={{ 
                                    flex: 1,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'flex-start',
                                    background: 'rgba(255,255,255,0.02)',
                                    borderRadius: '16px',
                                    padding: '1rem',
                                    overflow: 'hidden', // CRITICAL: No scroll on parent
                                    minHeight: 0
                                }}>
                                    {/* The "Paper" - Scaling to fit height */}
                                    <div style={{ 
                                        height: '100%',
                                        aspectRatio: '1 / 1.414',
                                        background: invoiceSettings.layoutId === '3' ? 'white' : (invoiceSettings.layoutId === '2' ? '#fcfcfc' : 'white'), 
                                        borderRadius: '4px', 
                                        color: '#2c3e50', 
                                        boxShadow: '0 20px 50px rgba(0,0,0,0.5)', 
                                        position: 'relative', 
                                        display: 'flex', 
                                        flexDirection: 'column',
                                        fontSize: 'clamp(8px, 1.2vh, 11px)', // Responsive font
                                        overflow: 'hidden',
                                        maxWidth: '100%',
                                        border: invoiceSettings.layoutId === '3' ? '1px solid #eee' : 'none'
                                    }}>
                                        {/* LAYOUT 1 & 4 DECORATIVE BARS */}
                                        {(invoiceSettings.layoutId === '1' || invoiceSettings.layoutId === '4') && (
                                            <>
                                                <div style={{ height: '0.8%', background: invoiceSettings.accentColor, width: '100%' }}></div>
                                                <div style={{ height: '0.4%', background: invoiceSettings.primaryColor, width: '100%' }}></div>
                                            </>
                                        )}

                                        {/* LAYOUT 2 MODERN SIDEBAR/TOP */}
                                        {invoiceSettings.layoutId === '2' && (
                                            <div style={{ height: '20%', background: invoiceSettings.primaryColor, width: '100%', position: 'absolute', top: 0, zIndex: 0 }}></div>
                                        )}

                                        <div style={{ padding: '4% 6%', flex: 1, display: 'flex', flexDirection: 'column', gap: '3%', position: 'relative', zIndex: 1 }}>
                                            {/* Header Section */}
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: invoiceSettings.layoutId === '2' ? '5%' : '0' }}>
                                                <div style={{ display: 'flex', gap: '0.8rem', alignItems: 'center' }}>
                                                    {invoiceSettings.showLogo && restaurantData?.logo ? (
                                                        <img src={restaurantData.logo} alt="Logo" style={{ height: '3.5vh', objectFit: 'contain', filter: (invoiceSettings.layoutId === '2' && !restaurantData.logo.startsWith('data:image')) ? 'brightness(0) invert(1)' : 'none' }} />
                                                    ) : (
                                                        <div style={{ width: '2rem', height: '2rem', borderRadius: '4px', background: invoiceSettings.layoutId === '2' ? 'rgba(255,255,255,0.1)' : '#f8f9fa', border: invoiceSettings.layoutId === '2' ? '1px dashed rgba(255,255,255,0.2)' : '1px dashed #ddd' }}></div>
                                                    )}
                                                    <div>
                                                        <h3 style={{ margin: 0, fontSize: '1.4em', fontWeight: '900', color: invoiceSettings.layoutId === '2' ? 'white' : invoiceSettings.primaryColor, lineHeight: 1 }}>{restaurantData?.name || 'TastyFlow'}</h3>
                                                        <div style={{ fontSize: '0.6em', color: invoiceSettings.layoutId === '2' ? 'rgba(255,255,255,0.7)' : invoiceSettings.accentColor, fontWeight: '700', marginTop: '2px', textTransform: 'uppercase' }}>{invoiceSettings.invoiceTagline || 'Premium Dining Experience'}</div>
                                                    </div>
                                                </div>
                                                <div style={{ 
                                                    background: invoiceSettings.layoutId === '3' ? 'transparent' : (invoiceSettings.layoutId === '2' ? 'rgba(255,255,255,0.1)' : invoiceSettings.primaryColor), 
                                                    color: invoiceSettings.layoutId === '3' ? 'black' : 'white', 
                                                    padding: '0.4rem 0.8rem', 
                                                    borderRadius: '4px', 
                                                    textAlign: invoiceSettings.layoutId === '3' ? 'right' : 'center',
                                                    border: invoiceSettings.layoutId === '3' ? '1px solid #eee' : 'none'
                                                }}>
                                                    <div style={{ fontSize: '0.7em', fontWeight: '900', letterSpacing: '0.5px' }}>INVOICE</div>
                                                    <div style={{ fontSize: '0.6em', opacity: 0.8 }}>#1024</div>
                                                </div>
                                            </div>

                                            {invoiceSettings.layoutId !== '2' && <div style={{ height: '1px', background: '#eee' }}></div>}

                                            {/* Info Cards Row */}
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4%' }}>
                                                <div style={{ 
                                                    background: invoiceSettings.layoutId === '3' ? 'transparent' : '#f8f9fa', 
                                                    padding: invoiceSettings.layoutId === '3' ? '0' : '0.6rem', 
                                                    borderRadius: '6px', 
                                                    borderLeft: invoiceSettings.layoutId === '3' ? 'none' : `3px solid ${invoiceSettings.accentColor}`,
                                                    borderBottom: invoiceSettings.layoutId === '3' ? '1px solid #eee' : 'none',
                                                    paddingBottom: invoiceSettings.layoutId === '3' ? '0.5rem' : '0.6rem'
                                                }}>
                                                    <div style={{ fontSize: '0.55em', color: invoiceSettings.accentColor, fontWeight: '900', marginBottom: '0.3rem' }}>BILL TO</div>
                                                    <div style={{ fontWeight: '800', fontSize: '0.8em' }}>Dhruv Chavda</div>
                                                    <div style={{ fontSize: '0.65em', color: '#7f8c8d', opacity: 0.8 }}>Rajkot, Gujarat</div>
                                                </div>
                                                <div style={{ 
                                                    background: invoiceSettings.layoutId === '3' ? 'transparent' : '#f8f9fa', 
                                                    padding: invoiceSettings.layoutId === '3' ? '0' : '0.6rem', 
                                                    borderRadius: '6px', 
                                                    borderRight: invoiceSettings.layoutId === '3' ? 'none' : `3px solid ${invoiceSettings.primaryColor}`,
                                                    borderBottom: invoiceSettings.layoutId === '3' ? '1px solid #eee' : 'none',
                                                    paddingBottom: invoiceSettings.layoutId === '3' ? '0.5rem' : '0.6rem',
                                                    textAlign: invoiceSettings.layoutId === '3' ? 'right' : 'left'
                                                }}>
                                                    <div style={{ fontSize: '0.55em', color: invoiceSettings.primaryColor, fontWeight: '900', marginBottom: '0.3rem' }}>ISSUER</div>
                                                    <div style={{ fontWeight: '800', fontSize: '0.8em' }}>{restaurantData?.name || 'TastyFlow'}</div>
                                                    {invoiceSettings.contactVisible && (
                                                        <div style={{ fontSize: '0.65em', color: '#7f8c8d', opacity: 0.8, whiteSpace: 'nowrap', overflow: 'hidden' }}>{restaurantData?.location || 'Main Branch'}</div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Header Message */}
                                            {invoiceSettings.headerText && (
                                                <div style={{ 
                                                    background: invoiceSettings.layoutId === '3' ? 'transparent' : `${invoiceSettings.accentColor}08`, 
                                                    borderLeft: `2px solid ${invoiceSettings.accentColor}`, 
                                                    padding: '0.4rem 0.8rem', 
                                                    borderRadius: '4px',
                                                    fontStyle: invoiceSettings.layoutId === '3' ? 'italic' : 'normal'
                                                }}>
                                                    <div style={{ fontSize: '0.7em', fontWeight: '700', color: invoiceSettings.accentColor }}>{invoiceSettings.headerText}</div>
                                                </div>
                                            )}

                                            {/* Table */}
                                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                                <div style={{ 
                                                    display: 'grid', 
                                                    gridTemplateColumns: '2.5fr 1fr 1fr', 
                                                    background: invoiceSettings.layoutId === '3' ? 'white' : invoiceSettings.primaryColor, 
                                                    color: invoiceSettings.layoutId === '3' ? 'black' : 'white', 
                                                    padding: '0.4rem 0.8rem', 
                                                    borderRadius: '4px 4px 0 0', 
                                                    fontWeight: '800', 
                                                    fontSize: '0.6em',
                                                    borderBottom: invoiceSettings.layoutId === '3' ? '2px solid black' : 'none'
                                                }}>
                                                    <span>ITEM</span>
                                                    <span style={{ textAlign: 'center' }}>QTY</span>
                                                    <span style={{ textAlign: 'right' }}>TOTAL</span>
                                                </div>
                                                {[
                                                    { n: 'Mediterranean Salmon', q: 1, p: 850 },
                                                    { n: 'Truffle Risotto', q: 2, p: 840 },
                                                    { n: 'Berry Mocktail', q: 1, p: 180 }
                                                ].map((item, idx) => (
                                                    <div key={idx} style={{ 
                                                        display: 'grid', 
                                                        gridTemplateColumns: '2.5fr 1fr 1fr', 
                                                        padding: '0.4rem 0.8rem', 
                                                        background: idx % 2 === 0 ? '#fafafa' : 'white',
                                                        fontSize: '0.7em',
                                                        borderBottom: invoiceSettings.layoutId === '3' ? '1px solid #eee' : '1px solid #f0f0f0'
                                                    }}>
                                                        <span style={{ fontWeight: '600' }}>{item.n}</span>
                                                        <span style={{ textAlign: 'center', opacity: 0.6 }}>{item.q}</span>
                                                        <span style={{ textAlign: 'right', fontWeight: '700' }}>{item.p.toFixed(0)}</span>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Summary */}
                                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                <div style={{ 
                                                    background: invoiceSettings.layoutId === '3' ? 'transparent' : '#f8f9fa', 
                                                    padding: '0.6rem', 
                                                    borderRadius: '6px', 
                                                    minWidth: '45%',
                                                    border: invoiceSettings.layoutId === '3' ? '1px solid #eee' : 'none'
                                                }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.6em', color: '#7f8c8d' }}>
                                                        <span>Subtotal</span>
                                                        <span>1870.00</span>
                                                    </div>
                                                    <div style={{ height: '1px', background: '#eee', margin: '4px 0' }}></div>
                                                    <div style={{ 
                                                        display: 'flex', 
                                                        justifyContent: 'space-between', 
                                                        alignItems: 'center', 
                                                        background: invoiceSettings.layoutId === '3' ? 'white' : invoiceSettings.primaryColor, 
                                                        color: invoiceSettings.layoutId === '3' ? 'black' : 'white', 
                                                        padding: '0.3rem 0.5rem', 
                                                        borderRadius: '4px',
                                                        border: invoiceSettings.layoutId === '3' ? '2px solid black' : 'none'
                                                    }}>
                                                        <span style={{ fontSize: '0.55em', fontWeight: '900' }}>TOTAL</span>
                                                        <span style={{ fontSize: '0.9em', fontWeight: '900' }}>₹1963.50</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Footer */}
                                            <div style={{ 
                                                textAlign: 'center', 
                                                marginTop: 'auto', 
                                                paddingTop: '1rem', 
                                                borderTop: '1px dashed #ddd'
                                            }}>
                                                <div style={{ fontSize: '0.85em', fontWeight: '800', color: invoiceSettings.primaryColor, marginBottom: '2px' }}>{invoiceSettings.footerText || 'Thank you!'}</div>
                                                <div style={{ fontSize: '0.55em', color: '#999' }}>Computer generated invoice.</div>
                                            </div>
                                        </div>

                                        {/* LAYOUT 1 & 4 BOTTOM BAR */}
                                        {(invoiceSettings.layoutId === '1' || invoiceSettings.layoutId === '4') && (
                                            <div style={{ height: '1.2%', background: invoiceSettings.primaryColor, width: '100%' }}></div>
                                        )}
                                        
                                        {/* Design Preview Watermark */}
                                        <div style={{ 
                                            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%) rotate(-30deg)', 
                                            color: 'rgba(0,0,0,0.02)', fontSize: '3em', fontWeight: '900', pointerEvents: 'none', zIndex: 10 
                                        }}>PREVIEW</div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                )}
            </section>
          </div>
        </div>
      </main>

      <style>{`
        ::-webkit-scrollbar {
            width: 0px;
            background: transparent;
        }

        @keyframes pulse-glow {
          0% { box-shadow: 0 0 0 0 rgba(0, 209, 178, 0.4); }
          70% { box-shadow: 0 0 0 10px rgba(0, 209, 178, 0); }
          100% { box-shadow: 0 0 0 0 rgba(0, 209, 178, 0); }
        }

        .status-dot-active {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #00d1b2;
          animation: pulse-glow 2s infinite;
        }

        .subscription-card:hover {
          transform: translateY(-5px);
          border-color: rgba(0, 209, 178, 0.3) !important;
          box-shadow: 0 20px 40px rgba(0,0,0,0.4) !important;
        }

        .feature-tag {
          transition: all 0.2s;
        }

        .feature-tag:hover {
          background: rgba(255, 255, 255, 0.1) !important;
          transform: scale(1.05);
        }
      `}</style>
    </div>
  );
};

export default Settings;
